import React from 'react';
import { Link } from 'react-router-dom';
import { homeService } from '../../_services';
import { authenticatedIdentity } from '../../_services/webSession';
import { Form, FormGroup, FormControl, Modal, Button } from 'react-bootstrap';
import moment from 'moment';
import config from '../../../config';
import Select, { components } from 'react-select';
import {
    cacheMonthlyTargetsInLocalStorage,
    getCurrentMonthTarget,
    loadMonthlyTargetsFromAccount,
} from '../../utils/monthlyTargets';
import DatacoLoadingIndicator from '../../_components/DatacoLoadingIndicator';
import AuroraTrends from '../../_components/AuroraTrendChart';
import { FloorDeck } from './FloorDeck';
import { HomePageTabs } from './HomePageTabs';
import {
    SELECT_ALL_MACHINE_VALUE,
    getAppliedDailyMachineValues,
    getDailyMachineOptions,
    getDailyMachineSelectionSummary,
    sameMachineValues,
    toggleAllDailyMachines,
} from './dailyMachinePicker';

const DailyMachineOption = (props) => {
    const isSelectAll = props.data.value === SELECT_ALL_MACHINE_VALUE;
    const isChecked = isSelectAll
        ? props.selectProps.dailyMachineAllSelected
        : props.isSelected;

    return (
        <components.Option {...props}>
            <input
                type="checkbox"
                checked={isChecked}
                readOnly
                tabIndex={-1}
                aria-hidden="true"
                style={{ marginRight: '8px' }}
            />
            {props.children}
        </components.Option>
    );
};

const DailyMachineValueContainer = (props) => {
    const { dailyMachineSummary } = props.selectProps;

    return (
        <components.ValueContainer {...props}>
            {dailyMachineSummary && (
                <span style={{ marginLeft: '2px', whiteSpace: 'nowrap' }}>
                    {dailyMachineSummary}
                </span>
            )}
            {props.children}
        </components.ValueContainer>
    );
};

class HomePage extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            activeTab: props.pageMode === 'daily-overview' ? 'pep' : 'floor-deck', // 'floor-deck', 'pep', or 'inventory'
            viewMode: props.pageMode === 'daily-overview' ? 'daily' : 'yearly', // 'yearly' or 'daily'
            selectedYear: moment().year(),
            selectedCategory: 'All',
            // Yearly PE&P Aurora trend data
            pepTrends: [],
            pepTrendsLoading: false,
            selectedMachine: [],
            years: [],
            categories: [{ value: 'All', text: 'All' }], // Will be populated dynamically from machine descriptions
            originMachines: [],
            machines: [],
            machineStr: '',
            // Daily view state
            dailyMachineData: [],
            loadingDailyData: false,
            dailyMachineDraftValues: [],
            dailyMachineAppliedValues: [],
            target1: 0,
            // Inventory data
            rmOccupancyData: null,
            fgOccupancyData: null,
            rmLocations: [],
            fgLocations: [],
            rmLocationOccupancyData: [],
            fgLocationOccupancyData: [],
            selectedRmLocationFilter: 'ALL',
            selectedFgLocationFilter: 'ALL',
            loadingInventoryData: false,
            inventoryByLocation: null,
            inventoryValue: null,
            // Job info dialog state
            showJobDialog: false,
            jobDialogData: null,
            loadingJobInfo: false,
        };
        this.didChange = this.didChange.bind(this);
        this.fetchDailyMachineStats = this.fetchDailyMachineStats.bind(this);
        this.getToday = this.getToday.bind(this);
        this.fetchJobInfo = this.fetchJobInfo.bind(this);
        this.getLedClass = this.getLedClass.bind(this);
        this.handleWorkspaceChange = this.handleWorkspaceChange.bind(this);
        this.loadMachines = this.loadMachines.bind(this);
        this.handleDailyMachineChange = this.handleDailyMachineChange.bind(this);
        this.applyDailyMachineSelection = this.applyDailyMachineSelection.bind(this);
    }

    getToday() {
        let today = new Date();
        let dd = (today.getDate() < 10 ? '0' : '') + today.getDate();
        let mm = (today.getMonth() < 9 ? '0' : '') + (today.getMonth() + 1);
        let yyyy = today.getFullYear();
        return yyyy + '-' + mm + '-' + dd;
    }

    handleWorkspaceChange(nextTab, nextViewMode, categoryName) {
        if (nextTab === this.state.activeTab && (!nextViewMode || nextViewMode === this.state.viewMode) && !categoryName) return;

        const viewMode = nextViewMode || this.state.viewMode;
        const selectedCategory = categoryName || this.state.selectedCategory;
        const machines = this.filterMachines(selectedCategory, viewMode);
        const nextState = {
            activeTab: nextTab,
            viewMode,
        };

        if (viewMode !== this.state.viewMode || categoryName) {
            Object.assign(nextState, {
                selectedCategory,
                machines,
                machineStr: machines.map((machine) => machine.value).join(','),
                selectedMachine: [],
                dailyMachineDraftValues: [],
                dailyMachineAppliedValues: getAppliedDailyMachineValues([], machines),
            });
        }

        this.setState(nextState, () => {
            if (nextTab === 'inventory') {
                this.fetchInventoryData();
                return;
            }

            if (nextTab === 'pep') {
                if (this.state.viewMode === 'daily') {
                    this.loadMachines();
                } else {
                    this.didChange();
                }
            }
        });
    }

    componentDidMount() {
        this.targetsMounted = true;
        const targetIdentity = authenticatedIdentity();
        window.addEventListener('focus', this.loadMachines);
        let yearArray = [];
        for (let i = this.state.selectedYear; i >= 2019; i--) {
            yearArray.push(i);
            this.setState({
                years: yearArray
            });
        }
        const requestOptions = {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        };

        if (localStorage.getItem('user') != null) {
            fetch(`${config.apiUrl}/getAccountTarget?account_id=${JSON.parse(localStorage.getItem('user')).accountId}`, requestOptions)
                .then((response) => {
                    return response.text().then(text => {
                        const data = text && JSON.parse(text);

                        return data;
                    });
                })
                .then(account => {
                    if (!this.targetsMounted || targetIdentity !== authenticatedIdentity()) return;

                    const { oee, productivity } = loadMonthlyTargetsFromAccount(account);
                    cacheMonthlyTargetsInLocalStorage(oee, productivity, targetIdentity);
                    const currentOee = getCurrentMonthTarget(oee);
                    this.setState({ target1: currentOee });

                }).catch(() => {
                    // Keep default targets when the display lookup is unavailable.
                });


            this.loadMachines();
        }
        // Check active tab and draw appropriate charts
        if (this.state.activeTab === 'inventory') {
            this.fetchInventoryData();
        } else if (this.state.activeTab === 'pep') {
            this.didChange();
        }
    }

    componentWillUnmount() {
        this.targetsMounted = false;
        window.removeEventListener('focus', this.loadMachines);
        this.machineRequestId = (this.machineRequestId || 0) + 1;
        this.dailyRequestId = (this.dailyRequestId || 0) + 1;
        this.pepTrendRequestId = (this.pepTrendRequestId || 0) + 1;
    }

    loadMachines() {
        if (!localStorage.getItem('user')) return;

        const accountId = JSON.parse(localStorage.getItem('user')).accountId;
        const requestId = (this.machineRequestId || 0) + 1;
        this.machineRequestId = requestId;

        return fetch(`${config.apiUrl}/getAllMachines?active=all&company=${accountId}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        })
            .then(response => response.text())
            .then(text => {
                if (requestId !== this.machineRequestId) return;

                const originMachines = (JSON.parse(text || '[]') || []).map(machine => ({
                    id: machine.id,
                    label: machine.machine,
                    value: machine.machine,
                    category: machine.category,
                    description: machine.description,
                    active: machine.active,
                }));
                const descriptions = [...new Set(
                    originMachines.map(machine => machine.description).filter(Boolean)
                )];
                const machines = this.filterMachines(this.state.selectedCategory, this.state.viewMode, originMachines);
                const allowedValues = new Set(machines.map(machine => machine.value));
                const dailyMachineDraftValues = this.state.dailyMachineDraftValues.filter(value => allowedValues.has(value));
                const dailyMachineAppliedValues = getAppliedDailyMachineValues(
                    this.state.dailyMachineAppliedValues.filter(value => allowedValues.has(value)),
                    machines
                );

                this.setState({
                    originMachines,
                    machines,
                    categories: [
                        { value: 'All', text: 'All' },
                        ...descriptions.map(description => ({ value: description, text: description })),
                    ],
                    dailyMachineDraftValues,
                    dailyMachineAppliedValues,
                }, () => {
                    if (this.state.activeTab === 'pep' && this.state.viewMode === 'daily') {
                        this.fetchDailyMachineStats(dailyMachineAppliedValues);
                    }
                });
            })
            .catch(error => console.error('Error fetching machines:', error));
    }

    didChange() {
        if (this.state.activeTab === 'pep') {
            // Tear down any inventory Chart.js instances/tooltips before showing PE&P.
            homeService.clearAllCharts();
            if (this.state.viewMode === 'yearly') {
                this.loadPepTrends();
            }
        } else if (this.state.activeTab === 'inventory') {
            // Inventory charts will be drawn here
            this.drawInventoryCharts();
        }
    }

    loadPepTrends() {
        const requestId = (this.pepTrendRequestId || 0) + 1;
        this.pepTrendRequestId = requestId;
        const result = homeService.getYearlyPepTrends(this.state);

        if (result.data) {
            this.setState({ pepTrends: result.data, pepTrendsLoading: false });
        } else {
            this.setState({ pepTrendsLoading: true });
        }

        result.revalidate
            .then((metrics) => {
                if (this.pepTrendRequestId === requestId) {
                    this.setState({ pepTrends: metrics, pepTrendsLoading: false });
                }
            })
            .catch((error) => {
                console.error('Error fetching yearly PE&P trends:', error);
                if (this.pepTrendRequestId !== requestId) return;
                this.setState({
                    pepTrends: result.data || [],
                    pepTrendsLoading: false,
                });
            });
    }

    fetchInventoryData() {
        if (!localStorage.getItem('user')) return;

        const accountId = JSON.parse(localStorage.getItem('user')).accountId;
        const requestOptions = {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        };

        this.setState({ loadingInventoryData: true });
        Promise.all([
            fetch(`${config.apiUrl}/getRMWarehouseOccupancyReport?company=${accountId}`, requestOptions).then((response) => response.text().then(text => text && JSON.parse(text))).catch(() => null),
            fetch(`${config.apiUrl}/getFGWarehouseOccupancyReport?company=${accountId}`, requestOptions).then((response) => response.text().then(text => text && JSON.parse(text))).catch(() => null),
            fetch(`${config.apiUrl}/getInventoryByLocation?company=${accountId}`, requestOptions).then((response) => response.text().then(text => text && JSON.parse(text))).catch(() => null),
            fetch(`${config.apiUrl}/getInventoryValueByLocation?company=${accountId}`, requestOptions).then((response) => response.text().then(text => text && JSON.parse(text))).catch(() => null),
            fetch(`${config.apiUrl}/getMasterLocations?company=${accountId}&area=${encodeURIComponent('RM Warehouse')}`, requestOptions).then((response) => response.text().then(text => text && JSON.parse(text))).catch(() => []),
            fetch(`${config.apiUrl}/getMasterLocations?company=${accountId}&area=${encodeURIComponent('FG Warehouse')}`, requestOptions).then((response) => response.text().then(text => text && JSON.parse(text))).catch(() => [])
        ])
            .then(async ([rmData, fgData, locationData, valueData, rmLocations, fgLocations]) => {
                const safeRmLocations = Array.isArray(rmLocations) ? rmLocations : [];
                const safeFgLocations = Array.isArray(fgLocations) ? fgLocations : [];

                const rmLocationOccupancyData = await Promise.all(
                    safeRmLocations.map(async (location) => {
                        const code = location.locationCode;
                        if (!code) return null;
                        try {
                            const data = await fetch(`${config.apiUrl}/getRMWarehouseOccupancyReport?company=${accountId}&locationCode=${encodeURIComponent(code)}`, requestOptions)
                                .then((response) => response.text().then(text => text && JSON.parse(text)));
                            return { locationCode: code, data };
                        } catch (error) {
                            return null;
                        }
                    })
                );

                const fgLocationOccupancyData = await Promise.all(
                    safeFgLocations.map(async (location) => {
                        const code = location.locationCode;
                        if (!code) return null;
                        try {
                            const data = await fetch(`${config.apiUrl}/getFGWarehouseOccupancyReport?company=${accountId}&locationCode=${encodeURIComponent(code)}`, requestOptions)
                                .then((response) => response.text().then(text => text && JSON.parse(text)));
                            return { locationCode: code, data };
                        } catch (error) {
                            return null;
                        }
                    })
                );

                const nextRmFilter =
                    safeRmLocations.length === 1 && safeRmLocations[0].locationCode
                        ? safeRmLocations[0].locationCode
                        : 'ALL';
                const nextFgFilter =
                    safeFgLocations.length === 1 && safeFgLocations[0].locationCode
                        ? safeFgLocations[0].locationCode
                        : 'ALL';

                this.setState({
                    rmOccupancyData: rmData,
                    fgOccupancyData: fgData,
                    inventoryByLocation: locationData,
                    inventoryValue: valueData,
                    rmLocations: safeRmLocations,
                    fgLocations: safeFgLocations,
                    rmLocationOccupancyData: rmLocationOccupancyData.filter(Boolean),
                    fgLocationOccupancyData: fgLocationOccupancyData.filter(Boolean),
                    loadingInventoryData: false,
                    selectedRmLocationFilter: nextRmFilter,
                    selectedFgLocationFilter: nextFgFilter
                }, () => {
                    setTimeout(() => this.drawInventoryCharts(), 100);
                });
            })
            .catch(error => {
                console.error('Error fetching inventory dashboard data:', error);
                this.setState({ loadingInventoryData: false });
            });
    }

    drawInventoryCharts() {
        // Clear existing charts
        homeService.clearAllCharts();

        const { rmOccupancyData, fgOccupancyData, inventoryByLocation, inventoryValue, rmLocationOccupancyData, fgLocationOccupancyData, selectedRmLocationFilter, selectedFgLocationFilter } = this.state;

        let hasData = false;

        // Draw RM Warehouse Occupancy Rate (%)
        if (rmOccupancyData && rmOccupancyData.totals && rmOccupancyData.occupancyRate) {
            const rmData = [
                { label: 'Good Parts', value: rmOccupancyData.totals.goodParts || 0 },
                { label: 'Free', value: rmOccupancyData.totals.freeLocations || 0 },
                { label: 'On Hold', value: rmOccupancyData.totals.onHold || 0 }
            ];
            const rmOccupancyRate = rmOccupancyData.occupancyRate.percentage || 0;
            homeService.drawInventoryChart('inventory-rm', 'RM Warehouse Occupancy Rate (%)', rmData, '', true); // true = occupancy chart
            hasData = true;
        } else {
            homeService.drawInventoryChart('inventory-rm', 'RM Warehouse Occupancy Rate (%)', [], 'Loading...');
        }

        // Draw FG Warehouse Occupancy Rate (%) - same structure as RM
        if (fgOccupancyData && fgOccupancyData.totals && fgOccupancyData.occupancyRate) {
            const fgData = [
                { label: 'Good Parts', value: fgOccupancyData.totals.goodParts || 0 },
                { label: 'Free', value: fgOccupancyData.totals.freeLocations || 0 },
                { label: 'On Hold', value: fgOccupancyData.totals.onHold || 0 }
            ];
            const fgOccupancyRate = fgOccupancyData.occupancyRate.percentage || 0;
            homeService.drawInventoryChart('inventory-fg', 'FG Warehouse Occupancy Rate (%)', fgData, '', true); // true = occupancy chart
            hasData = true;
        } else {
            homeService.drawInventoryChart('inventory-fg', 'FG Warehouse Occupancy Rate (%)', [], 'Loading...');
        }

        // Draw Total Inventory in Locations
        if (inventoryByLocation && inventoryByLocation.data && inventoryByLocation.data.length > 0) {
            const locationData = inventoryByLocation.data.map(item => ({
                label: item.locationCode || 'Unknown',
                value: item.quantity || 0
            }));
            const totalPcs = inventoryByLocation.totalPcs || 0;
            homeService.drawInventoryChart('inventory-locations', 'Total Inventory in Locations', locationData, `Total in Plant (Pcs): ${totalPcs.toLocaleString()}`);
            hasData = true;
        } else {
            homeService.drawInventoryChart('inventory-locations', 'Total Inventory in Locations', [], 'Loading...');
        }

        // Draw Total Inventory in Value (MYR)
        if (inventoryValue && inventoryValue.data && inventoryValue.data.length > 0) {
            const valueData = inventoryValue.data.map(item => ({
                label: item.locationCode || 'Unknown',
                value: item.value || 0
            }));
            const totalMYR = inventoryValue.totalMYR || 0;
            homeService.drawInventoryChart('inventory-value', 'Total Inventory in Value (MYR)', valueData, `Total in Plant (MYR): ${totalMYR.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
            hasData = true;
        } else {
            homeService.drawInventoryChart('inventory-value', 'Total Inventory in Value (MYR)', [], 'Loading...');
        }

        // Draw RM occupancy by each location
        const filteredRmLocationData = (Array.isArray(rmLocationOccupancyData) ? rmLocationOccupancyData : []).filter((item) => {
            if (!item) return false;
            if (selectedRmLocationFilter === 'ALL') return true;
            return item.locationCode === selectedRmLocationFilter;
        });

        if (filteredRmLocationData.length > 0) {
            filteredRmLocationData.forEach((item, index) => {
                if (item && item.data && item.data.totals) {
                    const data = [
                        { label: 'Good Parts', value: item.data.totals.goodParts || 0 },
                        { label: 'Free', value: item.data.totals.freeLocations || 0 },
                        { label: 'On Hold', value: item.data.totals.onHold || 0 }
                    ];
                    homeService.drawInventoryChart(`inventory-rm-loc-${item.locationCode || index}`, `RM Occupancy (${item.locationCode})`, data, '', true);
                    hasData = true;
                }
            });
        }

        // Draw FG occupancy by each location
        const filteredFgLocationData = (Array.isArray(fgLocationOccupancyData) ? fgLocationOccupancyData : []).filter((item) => {
            if (!item) return false;
            if (selectedFgLocationFilter === 'ALL') return true;
            return item.locationCode === selectedFgLocationFilter;
        });

        if (filteredFgLocationData.length > 0) {
            filteredFgLocationData.forEach((item, index) => {
                if (item && item.data && item.data.totals) {
                    const data = [
                        { label: 'Good Parts', value: item.data.totals.goodParts || 0 },
                        { label: 'Free', value: item.data.totals.freeLocations || 0 },
                        { label: 'On Hold', value: item.data.totals.onHold || 0 }
                    ];
                    homeService.drawInventoryChart(`inventory-fg-loc-${item.locationCode || index}`, `FG Occupancy (${item.locationCode})`, data, '', true);
                    hasData = true;
                }
            });
        }

        // Clear loading state if we have at least some data
        if (hasData && this.state.loadingInventoryData) {
            this.setState({ loadingInventoryData: false });
        }
    }

    fetchDailyMachineStats(machineValues = this.state.dailyMachineAppliedValues) {
        const requestOptions = {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        };

        if (!localStorage.getItem('user')) return;

        const accountId = JSON.parse(localStorage.getItem('user')).accountId;
        const today = this.getToday();
        const dailyMachines = this.filterMachines(this.state.selectedCategory, 'daily');
        const machineStr = getAppliedDailyMachineValues(machineValues, dailyMachines).join(',');
        const requestId = (this.dailyRequestId || 0) + 1;
        this.dailyRequestId = requestId;

        if (!machineStr) {
            this.setState({ dailyMachineData: [], loadingDailyData: false });
            return;
        }

        this.setState({ loadingDailyData: true });

        const dailyStatsUrl = `${config.apiUrl}/getDailyMachineStats?company=${accountId}&date=${today}&machine=${machineStr.trim()}`;
        const dailyPepUrl = `${config.apiUrl}/getDailyPepMetrics?company=${accountId}&date=${today}&month=${today.slice(0, 7)}&machine=${encodeURIComponent(machineStr.trim())}`;
        Promise.all([
            fetch(dailyStatsUrl, requestOptions).then((response) => response.text().then(text => text && JSON.parse(text))),
            fetch(dailyPepUrl, requestOptions).then((response) => response.text().then(text => text && JSON.parse(text))),
        ])
            .then(([result, pepMetrics]) => {
                if (requestId !== this.dailyRequestId) return;

                const pepByMachine = new Map((pepMetrics && Array.isArray(pepMetrics.machines) ? pepMetrics.machines : [])
                    .map((metric) => [metric.machine, metric]));
                const machineData = result.map(item => ({
                    machine: item.machine,
                    oee: parseFloat(pepByMachine.get(item.machine)?.oee) || 0,
                    productivity: parseFloat(item.productivity) || 0,
                    qloss: parseFloat(item.qloss) || 0,
                    tloss: parseFloat(item.tloss) || 0,
                    oloss: parseFloat(item.oloss) || 0,
                    progress: parseFloat(item.progress) || 0,
                    jobOrder: item.jobOrder,
                    partNo: item.partNo,
                    jobID: item.jobID,
                    totalActOutput: parseFloat(item.totalActOutput) || 0,
                    dataInterval: item.dataInterval || 0,
                    timestamp: item.timestamp + ''
                }));
                this.setState({
                    dailyMachineData: machineData,
                    loadingDailyData: false
                });
            })
            .catch(error => {
                if (requestId !== this.dailyRequestId) return;

                console.error('Error fetching daily machine stats:', error);
                this.setState({
                    dailyMachineData: [],
                    loadingDailyData: false
                });
            });
    }

    selectedBadgeClicked(optionsList) {
        let machineStr = '';

        for (let i = 0; i < optionsList.length; i++) {
            machineStr += optionsList[i].label + ',';
        }

        this.setState({ machineStr: machineStr.substring(0, machineStr.length - 1) });
        setTimeout(() => {
            this.didChange();
            if (this.state.viewMode === 'daily') {
                this.fetchDailyMachineStats();
            }

        }, 500);
    }

    filterMachines(selectedDescription, viewMode = this.state.viewMode, originMachines = this.state.originMachines) {
        return originMachines.filter(machine => {
            const matchesCategory = !selectedDescription || selectedDescription === 'All'
                || selectedDescription === 'select' || machine.description === selectedDescription;

            return matchesCategory && (viewMode !== 'daily' || machine.active !== false);
        });
    }

    handleDailyMachineChange(selectedOptions, actionMeta = {}) {
        const selectedValues = (selectedOptions || []).map((option) => option.value);
        const nextDraft = selectedValues.includes(SELECT_ALL_MACHINE_VALUE)
            ? toggleAllDailyMachines(this.state.dailyMachineDraftValues, this.state.machines)
            : selectedValues;
        const appliesImmediately = ['remove-value', 'pop-value', 'clear'].includes(actionMeta.action);

        this.setState({ dailyMachineDraftValues: nextDraft }, () => {
            if (appliesImmediately) this.applyDailyMachineSelection(nextDraft);
        });
    }

    applyDailyMachineSelection(draftValues = this.state.dailyMachineDraftValues) {
        const nextApplied = getAppliedDailyMachineValues(
            draftValues,
            this.state.machines
        );

        if (sameMachineValues(nextApplied, this.state.dailyMachineAppliedValues)) return;

        this.setState({ dailyMachineAppliedValues: nextApplied }, () => {
            this.fetchDailyMachineStats(nextApplied);
        });
    }

    getLedClass(data) {
        const hasData = data.totalActOutput && data.totalActOutput > 0;
        if (!hasData) return 'led-blank';

        if (data.dataInterval !== null && data.timestamp !== null && data.timestamp !== 'undefined' && data.timestamp !== 'null') {
            const isoTimestamp = data.timestamp.replace(' ', 'T').replace(' +', '+').replace(' -', '-');
            const tsTime = new Date(isoTimestamp).getTime();
            const staleThreshold = (data.dataInterval * 60 + 15) * 60 * 1000;
            const elapsed = Date.now() - tsTime;
            return elapsed > staleThreshold ? 'led-flash' : 'led-green';
        }
        return 'led-green';
    }

    getGaugeColor(oee) {
        const target = parseFloat(this.state.target1) || 0;
        if (oee >= target) return '#28a745'; // Green
        if (oee >= target - 10) return '#ffc107'; // Orange/Yellow
        return '#dc3545'; // Red
    }

    async fetchJobInfo(data) {
        if (!localStorage.getItem('user')) return;
        this.setState({ loadingJobInfo: true });

        const accountId = JSON.parse(localStorage.getItem('user')).accountId;
        const requestOptions = {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        };
        const today = this.getToday();

        try {
            const [planningResult, qtyResult, jsResult] = await Promise.all([
                fetch(`${config.apiUrl}/getPlanningAssociate?company=${accountId}&start=${today}`, requestOptions)
                    .then(res => res.json()),
                fetch(`${config.apiUrl}/getCurrentTotalQty?partNo=${encodeURIComponent(data.partNo || '')}&jobNo=${encodeURIComponent(data.jobOrder || '')}`, requestOptions)
                    .then(res => res.json())
                    .catch(() => []),
                fetch(`${config.apiUrl}/getJobQtyByJobNo?jobNo=${encodeURIComponent(data.jobOrder || '')}`, requestOptions)
                    .then(res => res.json())
                    .catch(() => ({}))
            ]);

            // Find next job from planning data, matched by the job sheet's machine/line
            // (the saved planning title can be out of date).
            const machine = String(data.machine || '').trim();
            const planningData = planningResult.filter(item => {
                const job = item.jobs || {};
                return Boolean(machine) && [job.machineNo, job.lineNo].some(value => String(value || '').trim() === machine);
            });
            let nextJob = {};
            for (let i = 0; i < planningData.length; i++) {
                if (planningData[i].jobID == data.jobID) {
                    nextJob = planningData[i + 1] || {};
                    break;
                }
            }

            const totalOutput = qtyResult?.[0]?.total_output || '0';
            const totalReject = qtyResult?.[0]?.total_reject || '0';
            const jsQuantity = jsResult?.jsQuantity || 'N/A';
            const remark = qtyResult?.[0]?.remark || '';

            this.setState({
                loadingJobInfo: false,
                showJobDialog: true,
                jobDialogData: {
                    jobOrder: data.jobOrder,
                    partNo: data.partNo || 'N/A',
                    nextPartNo: nextJob?.jobs?.master?.partNo || 'N/A',
                    jsQuantity,
                    totalOutput,
                    totalReject,
                    remark
                }
            });
        } catch (error) {
            console.error('Error fetching job info:', error);
            this.setState({ loadingJobInfo: false });
        }
    }

    renderMachineCard(data, index) {
        const oeeValue = parseFloat(data.oee) || 0;
        const gaugeColor = this.getGaugeColor(oeeValue);
        const angle = (oeeValue / 100) * 180;
        const dashValue = (angle / 180) * 251.2;
        const progressValue = parseFloat(data.progress) || 0;
        const ledClass = this.getLedClass(data);

        // LED color mapping
        const ledColors = {
            'led-green': { bg: '#28a745', shadow: '0 0 4px #28a745' },
            'led-blank': { bg: '#e8e8e8', shadow: '0 0 4px #e8e8e8' },
            'led-flash': { bg: '#ffa807ff', shadow: '0 0 4px #ffa807ff', animation: 'ledBlink 1s ease-in-out infinite' },
        };
        const ledStyle = {
            ...styles.led,
            backgroundColor: ledColors[ledClass]?.bg || '#e8e8e8',
            boxShadow: ledColors[ledClass]?.shadow || '0 0 4px #e8e8e8',
            animation: ledColors[ledClass]?.animation || 'none',
        };

        return (
            <div key={index} style={styles.machineCard}>
                {/* LED animation keyframes */}
                <style>
                    {`
                        @keyframes fillGauge${index} {
                            from { stroke-dasharray: 0 251.2; }
                            to { stroke-dasharray: ${dashValue} 251.2; }
                        }
                        .gauge-arc-${index} {
                            animation: fillGauge${index} 1s ease-in forwards;
                        }
                        @keyframes ledBlink {
                            0%, 100% { opacity: 1; }
                            50% { opacity: 0.2; }
                        }
                    `}
                </style>
                <div style={styles.machineHeader}>
                    <span style={ledStyle}></span>
                    {data.machine}
                </div>
                <div style={styles.cardContent}>
                    {/* Clickable Job No section */}
                    <div
                        style={styles.jobNoDisplay}
                        onClick={() => this.fetchJobInfo(data)}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#f0f7ff';
                            e.currentTarget.style.borderColor = '#c0d1eb';
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#f8fbff';
                            e.currentTarget.style.borderColor = '#e0e6ed';
                            e.currentTarget.style.boxShadow = 'none';
                        }}
                    >
                        <div style={styles.jobNoLabel}>JOB NO</div>
                        <div style={styles.jobNoValue}>{data.jobOrder || 'N/A'}</div>
                    </div>
                    <div style={styles.gaugeContainer}>
                        <svg viewBox="0 0 200 120" style={styles.gauge}>
                            {/* Background arc (gray) */}
                            <path
                                d="M 20 100 A 80 80 0 0 1 180 100"
                                fill="none"
                                stroke="#e0e0e0"
                                strokeWidth="15"
                                strokeLinecap="round"
                            />
                            {/* Value arc (colored) with animation */}
                            <path
                                className={`gauge-arc-${index}`}
                                d="M 20 100 A 80 80 0 0 1 180 100"
                                fill="none"
                                stroke={gaugeColor}
                                strokeWidth="15"
                                strokeLinecap="round"
                            />
                            {/* Center text */}
                            <text x="100" y="85" textAnchor="middle" style={styles.gaugeLabel}>OEE</text>
                            <text x="100" y="110" textAnchor="middle" style={styles.gaugeValue}>{oeeValue.toFixed(1)}%</text>
                        </svg>
                    </div>
                    <div style={styles.metrics}>
                        <div style={styles.metricRow}>
                            <span style={styles.metricLabel}>Productivity</span>
                            <span style={styles.metricValue}>{parseFloat(data.productivity || 0).toFixed(1)}%</span>
                        </div>
                        <div style={styles.metricRow}>
                            <span style={styles.metricLabel}>Quality Loss</span>
                            <span style={styles.metricValue}>{parseFloat(data.qloss || 0).toFixed(1)}%</span>
                        </div>
                        <div style={styles.metricRow}>
                            <span style={styles.metricLabel}>Tech. Loss</span>
                            <span style={styles.metricValue}>{parseFloat(data.tloss || 0).toFixed(1)}%</span>
                        </div>
                        <div style={styles.metricRow}>
                            <span style={styles.metricLabel}>Org. Loss</span>
                            <span style={styles.metricValue}>{parseFloat(data.oloss || 0).toFixed(1)}%</span>
                        </div>
                    </div>
                    {/* Progress bar section */}
                    <div style={styles.progressSection}>
                        <div style={styles.progressHeader}>
                            <span style={styles.progressLabel}>Job Progress</span>
                            <span style={styles.progressValue}>{progressValue.toFixed(1)}%</span>
                        </div>
                        <div style={styles.progressBarContainer}>
                            <div style={{
                                ...styles.progressBarFill,
                                width: `${Math.min(progressValue, 100)}%`,
                            }}></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderDailyView() {
        const { loadingDailyData, dailyMachineData } = this.state;

        if (loadingDailyData) {
            return <DatacoLoadingIndicator loading={loadingDailyData} label="Loading machine data" />;
        }

        if (dailyMachineData.length === 0) {
            return (
                <div style={styles.emptyContainer}>
                    <p style={{ color: '#666' }}>No machine data available for today</p>
                </div>
            );
        }

        return (
            <div style={styles.machineGrid}>
                {dailyMachineData.map((data, index) => this.renderMachineCard(data, index))}
            </div>
        );
    }

    render() {
        if (this.state.activeTab === 'floor-deck') {
            return <FloorDeck activeView={this.state.activeTab} onViewChange={this.handleWorkspaceChange} />;
        }

        return (
            <main className="floor-deck floor-deck--workspace">
                <header className="floor-deck__header floor-deck__header--tabs-only">
                    <HomePageTabs activeView={this.state.activeTab} onViewChange={this.handleWorkspaceChange} />
                </header>
                <div className="home-page-workspace__content">
                        {/* Only show filters for PE&P tab */}
                        {this.state.activeTab === 'pep' && (
                            <div className="container mt-4">
                                <div className="row justify-content-center">
                                    <div className="col-auto">
                                        <Form className="row g-3 align-items-center">
                                            {/* Daily/Yearly Toggle */}
                                            <Form.Group className="col-auto d-flex align-items-center" controlId="formViewMode">
                                                <div style={styles.toggleContainer}>
                                                    <button
                                                        type="button"
                                                        style={{
                                                            ...styles.toggleButton,
                                                            ...(this.state.viewMode === 'daily' ? styles.toggleButtonActive : {})
                                                        }}
                                                        onClick={() => this.handleWorkspaceChange('pep', 'daily')}
                                                    >
                                                        Daily
                                                    </button>
                                                    <button
                                                        type="button"
                                                        style={{
                                                            ...styles.toggleButton,
                                                            ...(this.state.viewMode === 'yearly' ? styles.toggleButtonActive : {})
                                                        }}
                                                        onClick={() => this.handleWorkspaceChange('pep', 'yearly')}
                                                    >
                                                        Yearly
                                                    </button>
                                                </div>
                                            </Form.Group>

                                            {/* Year - Only show in Yearly mode */}
                                            {this.state.viewMode === 'yearly' && (
                                                <Form.Group className="col-auto" controlId="formYear" style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
                                                    <Form.Label className="me-2" style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>Year:</Form.Label>
                                                    <Form.Select
                                                        value={this.state.selectedYear}
                                                        onChange={(e) => {
                                                            if (e.target.value !== "select") {
                                                                this.setState({ selectedYear: e.target.value });
                                                            } else {
                                                                this.setState({ selectedYear: undefined });
                                                            }
                                                            setTimeout(() => {
                                                                this.didChange();
                                                            }, 100);
                                                        }}
                                                    >
                                                        <option value="select">select</option>
                                                        {this.state.years.map((year) => (
                                                            <option key={year} value={year}>
                                                                {year}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            )}

                                            {/* Category */}
                                            <Form.Group className="col-auto" controlId="formCategory" style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
                                                <Form.Label className="me-2" style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>Category:</Form.Label>
                                                <Form.Select
                                                    value={this.state.selectedCategory || "select"}
                                                    onChange={(e) => {
                                                        const nextMachines = this.filterMachines(e.target.value);
                                                        const nextApplied = getAppliedDailyMachineValues([], nextMachines);
                                                        const selectedCategory = e.target.value !== "select"
                                                            ? e.target.value
                                                            : undefined;

                                                        this.setState({
                                                            selectedCategory,
                                                            machines: nextMachines,
                                                            machineStr: nextMachines.map((machine) => machine.value).join(','),
                                                            selectedMachine: [],
                                                            dailyMachineDraftValues: [],
                                                            dailyMachineAppliedValues: nextApplied
                                                        }, () => {
                                                            if (this.state.viewMode === 'daily') {
                                                                this.fetchDailyMachineStats(nextApplied);
                                                            } else {
                                                                this.didChange();
                                                            }
                                                        });
                                                    }}
                                                >
                                                    <option value="select">select</option>
                                                    {this.state.categories.filter(category => this.state.viewMode !== 'daily'
                                                        || category.value === 'All'
                                                        || this.state.originMachines.some(machine => machine.active !== false && machine.description === category.value)
                                                    ).map((category) => (
                                                        <option key={category.value} value={category.value}>
                                                            {category.text}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>

                                            {/* Items - Show in Yearly mode only when category != All */}
                                            {this.state.viewMode === 'yearly' && this.state.selectedCategory !== 'All' && this.state.selectedCategory !== 'select' && (
                                                <Form.Group className="col-auto d-flex align-items-center" controlId="formItems">
                                                    <Form.Label className="me-2">Items:</Form.Label>
                                                    <div style={{ minWidth: "300px" }}>
                                                        <Select
                                                            options={this.state.machines}
                                                            isMulti
                                                            onChange={this.selectedBadgeClicked.bind(this)}
                                                            className="basic-multi-select"
                                                            classNamePrefix="select"
                                                        />
                                                    </div>
                                                </Form.Group>
                                            )}

                                            {/* Machine Select - Show in Daily mode */}
                                            {this.state.viewMode === 'daily' && (
                                                <Form.Group className="col-auto d-flex align-items-center" controlId="formMachines">
                                                    <div style={{ minWidth: "250px" }}>
                                                        <Select
                                                            options={getDailyMachineOptions(this.state.machines)}
                                                            isMulti
                                                            closeMenuOnSelect={false}
                                                            hideSelectedOptions={false}
                                                            isClearable
                                                            value={this.state.machines.filter((machine) =>
                                                                this.state.dailyMachineDraftValues.includes(machine.value)
                                                            )}
                                                            onChange={this.handleDailyMachineChange}
                                                            onMenuClose={this.applyDailyMachineSelection}
                                                            components={{
                                                                Option: DailyMachineOption,
                                                                MultiValue: () => null,
                                                                ValueContainer: DailyMachineValueContainer
                                                            }}
                                                            dailyMachineAllSelected={
                                                                this.state.machines.length > 0
                                                                && this.state.machines.every((machine) =>
                                                                    this.state.dailyMachineDraftValues.includes(machine.value)
                                                                )
                                                            }
                                                            dailyMachineSummary={getDailyMachineSelectionSummary(
                                                                this.state.dailyMachineDraftValues,
                                                                this.state.machines
                                                            )}
                                                            placeholder="Select Machines..."
                                                            className="basic-multi-select"
                                                            classNamePrefix="select"
                                                        />
                                                    </div>
                                                </Form.Group>
                                            )}
                                        </Form>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Conditional rendering based on active tab */}
                        {this.state.activeTab === 'pep' ? (
                            <>
                                {/* PE&P Content - Conditional rendering based on view mode */}
                                {this.state.viewMode === 'yearly' ? (
                                    <div className="container mt-4" style={{ position: 'relative' }}>
                                        {this.state.pepTrendsLoading ? (
                                            <DatacoLoadingIndicator loading={true} label="Loading performance data" />
                                        ) : (
                                            <AuroraTrends
                                                metrics={this.state.pepTrends}
                                                accent="Cyan → Blue"
                                                glow={true}
                                                year={this.state.selectedYear}
                                            />
                                        )}
                                    </div>
                                ) : (
                                    <div className="container mt-4">
                                        {this.renderDailyView()}
                                    </div>
                                )}

                                {/* Job Info Dialog */}
                                <Modal show={this.state.showJobDialog} onHide={() => this.setState({ showJobDialog: false })} centered>
                                    <Modal.Header closeButton>
                                        <Modal.Title>Job Information</Modal.Title>
                                    </Modal.Header>
                                    <Modal.Body>
                                        {this.state.jobDialogData && (
                                            <div>
                                                <div style={styles.dialogRow}>
                                                    <span style={styles.dialogLabel}>Job No:</span>
                                                    <span style={styles.dialogValue}>{this.state.jobDialogData.jobOrder}</span>
                                                </div>
                                                <div style={styles.dialogRow}>
                                                    <span style={styles.dialogLabel}>Part No:</span>
                                                    <span style={styles.dialogValue}>{this.state.jobDialogData.partNo}</span>
                                                </div>
                                                <div style={styles.dialogRow}>
                                                    <span style={styles.dialogLabel}>Next Part No:</span>
                                                    <span style={styles.dialogValue}>{this.state.jobDialogData.nextPartNo}</span>
                                                </div>
                                                <div style={styles.dialogRow}>
                                                    <span style={styles.dialogLabel}>JS Quantity:</span>
                                                    <span style={styles.dialogValue}>{this.state.jobDialogData.jsQuantity}</span>
                                                </div>
                                                <div style={styles.dialogRow}>
                                                    <span style={styles.dialogLabel}>Total Actual Output:</span>
                                                    <span style={styles.dialogValue}>{this.state.jobDialogData.totalOutput}</span>
                                                </div>
                                                <div style={styles.dialogRow}>
                                                    <span style={styles.dialogLabel}>Total Reject:</span>
                                                    <span style={styles.dialogValue}>{this.state.jobDialogData.totalReject}</span>
                                                </div>
                                                {this.state.jobDialogData.remark && (
                                                    <div style={styles.dialogRow}>
                                                        <span style={styles.dialogLabel}>Remark:</span>
                                                        <span style={styles.dialogValue}>{this.state.jobDialogData.remark}</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </Modal.Body>
                                </Modal>

                                {/* Loading overlay for job info */}
                                {this.state.loadingJobInfo && (
                                    <DatacoLoadingIndicator loading={this.state.loadingJobInfo} variant="overlay" size="compact" label="Loading job information" />
                                )}
                            </>
                        ) : (
                            <>
                                {/* Inventory Content */}
                                <div className="container" style={{ position: 'relative' }}>
                                    <div className="row" style={{ marginBottom: '20px' }}>
                                        <div className="col-xs-12 col-sm-6 col-md-4">
                                            <Form.Group controlId="rmLocationFilter">
                                                <Form.Label style={{ fontWeight: 600 }}>RM Location Filter</Form.Label>
                                                <Form.Select
                                                    value={this.state.selectedRmLocationFilter}
                                                    onChange={(e) => {
                                                        this.setState({ selectedRmLocationFilter: e.target.value }, () => {
                                                            setTimeout(() => this.drawInventoryCharts(), 50);
                                                        });
                                                    }}
                                                >
                                                    <option value="ALL">All RM Locations</option>
                                                    {this.state.rmLocationOccupancyData.map((item) => (
                                                        <option key={`rm-opt-${item.locationCode}`} value={item.locationCode}>
                                                            {item.locationCode}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </div>
                                        <div className="col-xs-12 col-sm-6 col-md-4">
                                            <Form.Group controlId="fgLocationFilter">
                                                <Form.Label style={{ fontWeight: 600 }}>FG Location Filter</Form.Label>
                                                <Form.Select
                                                    value={this.state.selectedFgLocationFilter}
                                                    onChange={(e) => {
                                                        this.setState({ selectedFgLocationFilter: e.target.value }, () => {
                                                            setTimeout(() => this.drawInventoryCharts(), 50);
                                                        });
                                                    }}
                                                >
                                                    <option value="ALL">All FG Locations</option>
                                                    {this.state.fgLocationOccupancyData.map((item) => (
                                                        <option key={`fg-opt-${item.locationCode}`} value={item.locationCode}>
                                                            {item.locationCode}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </div>
                                    </div>
                                    <div className="row text-center">
                                        <div className="col-xs-12 col-sm-6 col-md-6" style={{ marginBottom: '30px' }}>
                                            <canvas id="inventory-rm" width="1" height="1"></canvas>
                                        </div>
                                        <div className="col-xs-12 col-sm-6 col-md-6" style={{ marginBottom: '30px' }}>
                                            <canvas id="inventory-fg" width="1" height="1"></canvas>
                                        </div>
                                        <div className="col-xs-12 col-sm-6 col-md-6" style={{ marginBottom: '30px' }}>
                                            <canvas id="inventory-locations" width="1" height="1"></canvas>
                                        </div>
                                        <div className="col-xs-12 col-sm-6 col-md-6" style={{ marginBottom: '30px' }}>
                                            <canvas id="inventory-value" width="1" height="1"></canvas>
                                        </div>
                                        {this.state.rmLocationOccupancyData
                                            .filter((item) => this.state.selectedRmLocationFilter === 'ALL' || item.locationCode === this.state.selectedRmLocationFilter)
                                            .map((item, index) => (
                                            <div key={`rm-loc-${item.locationCode || index}`} className="col-xs-12 col-sm-6 col-md-6" style={{ marginBottom: '30px' }}>
                                                <canvas id={`inventory-rm-loc-${item.locationCode || index}`} width="1" height="1"></canvas>
                                            </div>
                                        ))}
                                        {this.state.fgLocationOccupancyData
                                            .filter((item) => this.state.selectedFgLocationFilter === 'ALL' || item.locationCode === this.state.selectedFgLocationFilter)
                                            .map((item, index) => (
                                            <div key={`fg-loc-${item.locationCode || index}`} className="col-xs-12 col-sm-6 col-md-6" style={{ marginBottom: '30px' }}>
                                                <canvas id={`inventory-fg-loc-${item.locationCode || index}`} width="1" height="1"></canvas>
                                            </div>
                                        ))}
                                    </div>
                                    {this.state.loadingInventoryData && (
                                        <DatacoLoadingIndicator loading={this.state.loadingInventoryData} variant="overlay" label="Loading inventory data" />
                                    )}
                                </div>
                            </>
                        )}

                </div>
            </main>
        );
    }
}

// Styles object for Daily view
const styles = {
    sideTabContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        minWidth: '140px',
        paddingTop: '20px',
        paddingRight: '15px',
        borderRight: '2px solid #e9ecef',
    },
    sideTab: {
        padding: '15px 20px',
        border: 'none',
        backgroundColor: '#e9ecef',
        color: '#6c757d',
        cursor: 'pointer',
        fontSize: '15px',
        fontWeight: '600',
        borderRadius: '6px',
        transition: 'all 0.3s ease',
        textAlign: 'left',
        width: '100%',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
    },
    sideTabIcon: {
        fontSize: '20px',
        marginRight: '8px',
        verticalAlign: 'middle',
        flexShrink: 0,
    },
    sideTabText: {
        fontSize: '12px',
    },
    sideTabActive: {
        backgroundColor: '#28a745',
        color: '#fff',
        boxShadow: '0 4px 8px rgba(40,167,69,0.3)',
        transform: 'translateX(5px)',
    },
    toggleContainer: {
        display: 'flex',
        borderRadius: '8px',
        overflow: 'hidden',
        border: '1px solid #dee2e6',
    },
    toggleButton: {
        padding: '8px 20px',
        border: 'none',
        backgroundColor: '#f8f9fa',
        color: '#495057',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        transition: 'all 0.2s ease',
    },
    toggleButtonActive: {
        backgroundColor: '#007bff',
        color: '#fff',
    },
    todayBadge: {
        backgroundColor: '#3498db',
        color: 'white',
        padding: '10px 30px',
        borderRadius: '25px',
        fontSize: '16px',
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: '20px',
        display: 'inline-block',
        width: '100%',
    },
    machineGrid: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '15px',
        justifyContent: 'center',
    },
    machineCard: {
        background: 'white',
        border: '1px solid #ddd',
        borderRadius: '8px',
        width: 'calc(25% - 15px)',
        minWidth: '220px',
        maxWidth: '280px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },
    machineHeader: {
        background: '#f8f9fa',
        padding: '12px',
        fontWeight: 'bold',
        borderBottom: '1px solid #ddd',
        fontSize: '16px',
    },
    cardContent: {
        padding: '15px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    gaugeContainer: {
        width: '100%',
        maxWidth: '180px',
    },
    gauge: {
        width: '100%',
        height: 'auto',
    },
    gaugeLabel: {
        fontSize: '14px',
        fill: '#666',
    },
    gaugeValue: {
        fontSize: '18px',
        fontWeight: 'bold',
        fill: '#333',
    },
    metrics: {
        width: '100%',
        marginTop: '10px',
    },
    metricRow: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '6px 0',
        borderBottom: '1px solid #eee',
        fontSize: '13px',
    },
    metricLabel: {
        color: '#666',
    },
    metricValue: {
        fontWeight: '500',
    },
    // Progress bar
    progressSection: {
        width: '100%',
        paddingTop: '8px',
        borderTop: '1px solid #eee',
    },
    progressHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '6px',
    },
    progressLabel: {
        fontSize: '12px',
        color: '#666',
    },
    progressValue: {
        fontSize: '12px',
        fontWeight: 600,
        color: '#333',
    },
    progressBarContainer: {
        width: '100%',
        height: '8px',
        background: 'linear-gradient(to right, #e8f0fe, #f0f0f0)',
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.08)',
    },
    progressBarFill: {
        height: '100%',
        background: 'linear-gradient(90deg, #4facfe 0%, #00f2fe 50%, #38ef7d 100%)',
        borderRadius: '10px',
        transition: 'width 0.6s ease-out',
        boxShadow: '0 0 8px rgba(79, 172, 254, 0.4)',
    },
    // LED indicator
    led: {
        display: 'inline-block',
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        marginRight: '6px',
        verticalAlign: 'middle',
        flexShrink: 0,
    },
    // Job No section
    jobNoDisplay: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '10px',
        padding: '5px',
        background: '#f8fbff',
        borderRadius: '4px',
        border: '1px solid #e0e6ed',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        userSelect: 'none',
    },
    jobNoLabel: {
        fontSize: '9px',
        fontWeight: 700,
        color: '#8898aa',
        textTransform: 'uppercase',
        letterSpacing: '0.8px',
        lineHeight: 1,
        marginBottom: '2px',
    },
    jobNoValue: {
        fontSize: '13px',
        fontWeight: 700,
        color: '#32325d',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: '100%',
        textAlign: 'center',
        borderTop: '1px solid #e0e6ed',
        paddingTop: '2px',
    },
    // Dialog styles
    dialogRow: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '8px 0',
        borderBottom: '1px solid #f0f0f0',
    },
    dialogLabel: {
        color: '#666',
        fontWeight: 500,
    },
    dialogValue: {
        fontWeight: 600,
        color: '#333',
    },
    // Loading overlay
    loadingOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255,255,255,0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '300px',
    },
    emptyContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '300px',
    },
};

export { HomePage };
